import React from "react";
import {
  Box,
  Button,
  Card,
  Center,
  Container,
  Loader,
  TextInput,
  Text,
  Title,
  rem,
  Alert,
  PasswordInput,
  Textarea,
  Group,
} from "@mantine/core";
import TestDashboardNav from "../test_controller/dashboard/TestDashboardNav";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";
import { IconCircleCheck } from "@tabler/icons-react";
import { forgotPassword, resetPassword } from "../../utils/api-communication/RestApiCli";
import { isValidPassword } from "@utils/passwordUtils";

export default function ResetPasswordPage() {
  const [codeSent, setCodeSent] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [success, setSuccess] = React.useState(false);

  const { t } = useTranslation();

  const forgotPasswordForm = useForm({
    initialValues: {
      username: "",
    },
  });

  const resetPasswordForm = useForm({
    initialValues: {
      username: "",
      newPassword: "",
      token: "",
    },

    validate: {
      newPassword: (value) => (isValidPassword(value) ? null : t("Login.passwordRuleWarning")),
    },
  });

  const getErrorMessage = (error: unknown, fallbackKey: string): string => {
    const data = (error as any)?.response?.data;
    if (typeof data === "string") return data;
    if (typeof data?.message === "string") return data.message;
    return t(fallbackKey);
  };

  const handleForgotPassword = async (values) => {
    setLoading(true);
    setError(null);
    try {
      const response = await forgotPassword(values);
      if (response.status === 200) {
        setCodeSent(true);
      } else {
        setError(response.data);
      }
    } catch (error) {
      console.error(error);
      setError(getErrorMessage(error, "Login.forgotPasswordError"));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (values) => {
    setLoading(true);
    setError(null);
    try {
      const response = await resetPassword(values);
      if (response.status === 200) {
        setSuccess(true);
        setTimeout(() => {
          window.location.href = "/login";
        }, 5000);
      } else {
        setError(response.data);
      }
    } catch (error) {
      console.error(error);
      setError(getErrorMessage(error, "Login.resetPasswordError"));
    } finally {
      setLoading(false);
    }
  };

  const forgotPasswordComponent = () => {
    return (
      <form onSubmit={forgotPasswordForm.onSubmit(async (values) => handleForgotPassword(values))}>
        <TextInput
          mt={"md"}
          placeholder={t("enterUsername")}
          label={t("username")}
          withAsterisk
          key={forgotPasswordForm.key("username")}
          {...forgotPasswordForm.getInputProps("username")}
        />
        <Button mt={"md"} w={"100%"} type="submit">
          {loading ? <Loader color={"white"} size={"sm"} /> : t("Login.submit")}
        </Button>
      </form>
    );
  };

  const resetPasswordComponent = () => {
    return (
      <form onSubmit={resetPasswordForm.onSubmit(async (values) => handleResetPassword(values))}>
        <TextInput
          mt={"md"}
          placeholder={t("enterToken")}
          label={t("token")}
          readOnly={success}
          withAsterisk
          key={resetPasswordForm.key("token")}
          {...resetPasswordForm.getInputProps("token")}
        />
        <TextInput
          mt={"md"}
          placeholder={t("enterUsername")}
          label={t("username")}
          readOnly={success}
          withAsterisk
          key={resetPasswordForm.key("username")}
          {...resetPasswordForm.getInputProps("username")}
        />
        <PasswordInput
          mt={"md"}
          placeholder={t("Login.enterNewPassword")}
          label={t("Login.newPassword")}
          readOnly={success}
          withAsterisk
          key={resetPasswordForm.key("newPassword")}
          {...resetPasswordForm.getInputProps("newPassword")}
        />
        {success ? (
          <Alert
            mt={"md"}
            variant="light"
            color="green"
            title={t("success")}
            icon={<IconCircleCheck size={20} />}
          >
            <Group wrap={"nowrap"} justify="space-between" gap={0}>
              <Textarea variant="unstyled" autosize readOnly w={"100%"}>
                {t("Login.passwordResetSuccessDescription")}
              </Textarea>
              <Loader ml={"md"} size="sm" color="green" />
            </Group>
          </Alert>
        ) : (
          <Button mt={"md"} w={"100%"} type="submit">
            {loading ? <Loader color={"white"} size={"sm"} /> : t("Login.submit")}
          </Button>
        )}
      </form>
    );
  };

  return (
    <Box h={"100%"}>
      <TestDashboardNav />
      <Container h={"100%"}>
        <Center my="auto" mx="auto">
          <Card miw={rem(400)} mih={rem(200)} mt={rem(100)} withBorder shadow="xl" radius={"md"}>
            <Title mt={"sm"} size={"h4"}>
              {t("Login.resetPassword")}
            </Title>
            {codeSent ? resetPasswordComponent() : forgotPasswordComponent()}
            {error && (
              <Text mt={"sm"} c="red">
                {error}
              </Text>
            )}
          </Card>
        </Center>
      </Container>
    </Box>
  );
}
